/* ==========================================================================
   ROAMI — sections.js
   Motion and interaction for the editorial sections: line reveals, parallax,
   counters, the sticky scroll story, hotspots, the sticky CTA rail,
   accessory multi-add and the market selector.
   Depends on theme.js (loaded first) for Cart, Drawer and formatMoney.
   ========================================================================== */
(function () {
  'use strict';

  var theme = window.theme || {};

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function on(el, type, handler, options) {
    if (el) el.addEventListener(type, handler, options);
  }

  function motionOff() {
    return (
      theme.motion === 'off' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function money(cents) {
    return theme.formatMoney ? theme.formatMoney(cents) : (cents / 100).toFixed(2);
  }

  /* ---------------------------------------------------------- line reveal */

  function initLineReveal(scope) {
    var items = $$('.line-reveal', scope || document);
    if (!items.length) return;

    if (motionOff() || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );

    items.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------ parallax */

  var parallaxItems = [];
  var parallaxTicking = false;

  function updateParallax() {
    var viewportHeight = window.innerHeight;

    parallaxItems.forEach(function (item) {
      var rect = item.el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > viewportHeight + 200) return;
      var centre = rect.top + rect.height / 2 - viewportHeight / 2;
      item.el.style.transform = 'translate3d(0, ' + (-centre * item.speed).toFixed(2) + 'px, 0)';
    });

    parallaxTicking = false;
  }

  function initParallax(scope) {
    if (motionOff() || theme.parallax === false) return;

    $$('[data-parallax]', scope || document).forEach(function (el) {
      if (el.dataset.parallaxReady === 'true') return;
      el.dataset.parallaxReady = 'true';
      el.classList.add('parallax');
      parallaxItems.push({ el: el, speed: parseFloat(el.getAttribute('data-parallax')) || 0.12 });
    });

    if (!parallaxItems.length) return;
    updateParallax();
  }

  on(
    window,
    'scroll',
    function () {
      if (!parallaxItems.length || parallaxTicking) return;
      parallaxTicking = true;
      window.requestAnimationFrame(updateParallax);
    },
    { passive: true }
  );

  on(window, 'resize', function () {
    if (parallaxItems.length) window.requestAnimationFrame(updateParallax);
  });

  /* ------------------------------------------------------------ counters */

  function initCounters(scope) {
    var counters = $$('[data-count-to]', scope || document).filter(function (el) {
      return el.dataset.countReady !== 'true';
    });
    if (!counters.length) return;

    function paint(el, value) {
      var decimals = parseInt(el.getAttribute('data-count-decimals'), 10) || 0;
      el.textContent =
        (el.getAttribute('data-count-prefix') || '') +
        value.toFixed(decimals) +
        (el.getAttribute('data-count-suffix') || '');
    }

    function run(el) {
      el.dataset.countReady = 'true';
      var target = parseFloat(el.getAttribute('data-count-to')) || 0;

      if (motionOff()) {
        paint(el, target);
        return;
      }

      var duration = 1300;
      var start = null;

      function frame(now) {
        if (start === null) start = now;
        var progress = Math.min((now - start) / duration, 1);
        // easeOutExpo — fast start, long settle.
        var eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        paint(el, target * eased);
        if (progress < 1) window.requestAnimationFrame(frame);
      }

      window.requestAnimationFrame(frame);
    }

    if (!('IntersectionObserver' in window)) {
      counters.forEach(run);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          run(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    counters.forEach(function (el) {
      paint(el, 0);
      observer.observe(el);
    });
  }

  /* -------------------------------------------------------- scroll story */

  function initStory(scope) {
    $$('[data-story]', scope || document).forEach(function (root) {
      if (root.dataset.storyReady === 'true') return;
      root.dataset.storyReady = 'true';

      var steps = $$('[data-story-step]', root);
      var frames = $$('[data-story-frame]', root);
      var indexLabel = $('[data-story-index]', root);
      if (!steps.length) return;

      function activate(index) {
        steps.forEach(function (step, i) {
          step.classList.toggle('is-active', i === index);
        });
        frames.forEach(function (frame, i) {
          frame.classList.toggle('is-active', i === index);
        });
        if (indexLabel) {
          indexLabel.textContent = String(index + 1).padStart(2, '0') + ' / ' + String(steps.length).padStart(2, '0');
        }
      }

      activate(0);

      if (!('IntersectionObserver' in window)) {
        steps.forEach(function (step) {
          step.classList.add('is-active');
        });
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            activate(steps.indexOf(entry.target));
          });
        },
        { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
      );

      steps.forEach(function (step) {
        observer.observe(step);
      });
    });
  }

  /* ------------------------------------------------------------ hotspots */

  function initHotspots(scope) {
    $$('[data-hotspots]', scope || document).forEach(function (root) {
      if (root.dataset.hotspotsReady === 'true') return;
      root.dataset.hotspotsReady = 'true';

      var dots = $$('[data-hotspot]', root);
      var panels = $$('[data-hotspot-panel]', root);
      if (!dots.length) return;

      function activate(index) {
        dots.forEach(function (dot, i) {
          dot.setAttribute('aria-pressed', String(i === index));
        });
        panels.forEach(function (panel, i) {
          panel.classList.toggle('is-active', i === index);
        });
      }

      dots.forEach(function (dot, i) {
        on(dot, 'click', function () {
          activate(i);
        });
        on(dot, 'mouseenter', function () {
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) activate(i);
        });
      });

      panels.forEach(function (panel, i) {
        on(panel, 'mouseenter', function () {
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) activate(i);
        });
      });

      activate(0);
    });
  }

  /* ----------------------------------------------------- sticky CTA rail */

  function initRail(scope) {
    $$('[data-rail]', scope || document).forEach(function (rail) {
      if (rail.dataset.railReady === 'true') return;
      rail.dataset.railReady = 'true';

      var afterSelector = rail.getAttribute('data-rail-after');
      var sentinel = afterSelector ? $(afterSelector) : null;

      if (sentinel && 'IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              rail.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
            });
          },
          { threshold: 0 }
        );
        observer.observe(sentinel);
        return;
      }

      var ticking = false;
      function update() {
        rail.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.7);
        ticking = false;
      }
      on(
        window,
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(update);
        },
        { passive: true }
      );
      update();
    });
  }

  /* ------------------------------------------------------ accessory add */

  function initUpsell(scope) {
    $$('[data-upsell]', scope || document).forEach(function (root) {
      if (root.dataset.upsellReady === 'true') return;
      root.dataset.upsellReady = 'true';

      var inputs = $$('input[type="checkbox"]', root);
      var totalEl = $('[data-upsell-total]', root);
      var button = $('[data-upsell-add]', root);
      var countEl = $('[data-upsell-count]', root);
      if (!inputs.length || !button) return;

      function selected() {
        return inputs.filter(function (input) {
          return input.checked;
        });
      }

      function refresh() {
        var chosen = selected();
        var total = chosen.reduce(function (sum, input) {
          return sum + (parseInt(input.getAttribute('data-price'), 10) || 0);
        }, 0);
        if (totalEl) totalEl.textContent = money(total);
        if (countEl) countEl.textContent = chosen.length;
        button.disabled = chosen.length === 0;
      }

      inputs.forEach(function (input) {
        on(input, 'change', refresh);
      });

      on(button, 'click', function () {
        var chosen = selected();
        if (!chosen.length || !theme.Cart) return;

        theme.setButtonState(button, 'is-loading');

        theme.Cart.add(
          chosen.map(function (input) {
            return { id: parseInt(input.value, 10), quantity: 1 };
          })
        )
          .then(function () {
            theme.setButtonState(button, 'is-done');
            inputs.forEach(function (input) {
              input.checked = false;
            });
            setTimeout(function () {
              theme.setButtonState(button, null);
              refresh();
            }, 1500);
            if (theme.cartDrawer && theme.Drawer) theme.Drawer.open('CartDrawer');
          })
          .catch(function (error) {
            theme.setButtonState(button, null);
            if (theme.toast) theme.toast(error.message || 'Something went wrong.', 'alert');
          });
      });

      refresh();
    });
  }

  /* --------------------------------------------------- market selector -- */

  function initLocale(scope) {
    $$('[data-locale]', scope || document).forEach(function (root) {
      if (root.dataset.localeReady === 'true') return;
      root.dataset.localeReady = 'true';

      var button = $('[data-locale-button]', root);
      var form = root.closest('form') || $('form', root);

      on(button, 'click', function (event) {
        event.stopPropagation();
        root.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(root.classList.contains('is-open')));
      });

      $$('[data-locale-value]', root).forEach(function (option) {
        on(option, 'click', function () {
          var input = $('[data-locale-input]', root);
          if (input && form) {
            input.value = option.getAttribute('data-locale-value');
            form.submit();
          }
        });
      });
    });

    if (!document.localeCloseBound) {
      document.localeCloseBound = true;
      document.addEventListener('click', function (event) {
        $$('.locale.is-open').forEach(function (root) {
          if (!root.contains(event.target)) {
            root.classList.remove('is-open');
            var button = $('[data-locale-button]', root);
            if (button) button.setAttribute('aria-expanded', 'false');
          }
        });
      });
    }
  }

  /* ------------------------------------------------------ review bars --- */

  function initReviewBars(scope) {
    $$('[data-review-bars]', scope || document).forEach(function (root) {
      if (root.dataset.barsReady === 'true') return;
      root.dataset.barsReady = 'true';

      var fills = $$('[data-bar-fill]', root);

      function paint() {
        fills.forEach(function (fill) {
          fill.style.width = (fill.getAttribute('data-value') || 0) + '%';
        });
      }

      if (!('IntersectionObserver' in window)) {
        paint();
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            paint();
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.3 }
      );

      observer.observe(root);
    });
  }

  /* --------------------------------------------------------------- init  */

  /* ----------------------------------------------------------------- ruler
     Builds the chapter ruler from whatever panels the page happens to have,
     tracks which one you are on, and lends its colour to the header. Adding or
     removing a section in the customiser rebuilds it — nothing is hardcoded. */

  var rulerState = { panels: [], ticks: [], active: -1 };

  function panelIsDark(panel) {
    return panel.classList.contains('scheme-ink') || panel.classList.contains('scheme-graphite');
  }

  /* The chrome is however far down the page the header's bottom edge sits.
     Measuring the wrapper would also count the closed navigation drawer. */
  function measureChrome() {
    var header = document.querySelector('.header');
    if (!header) return;
    var rect = header.getBoundingClientRect();
    var h = Math.round(rect.bottom);
    if (h > 0 && h < window.innerHeight * 0.4) {
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
  }

  function paintChrome(panel) {
    var header = document.querySelector('[data-topbar]') || document.querySelector('.header');
    var ruler = document.querySelector('[data-ruler]');
    var dark = panel ? panelIsDark(panel) : null;
    [header, ruler].forEach(function (el) {
      if (!el) return;
      el.classList.toggle('is-on-dark', dark === true);
      el.classList.toggle('is-on-light', dark === false);
    });
  }

  function setActivePanel(index) {
    if (index === rulerState.active) return;
    rulerState.active = index;
    var panel = rulerState.panels[index];

    rulerState.ticks.forEach(function (tick, i) {
      tick.classList.toggle('is-active', i === index);
      if (i === index) tick.setAttribute('aria-current', 'true');
      else tick.removeAttribute('aria-current');
    });

    var now = document.querySelector('[data-ruler-now]');
    var count = document.querySelector('[data-ruler-count]');
    if (panel && now) now.textContent = panel.getAttribute('data-panel-label') || '';
    if (count) {
      count.textContent = pad(index + 1) + ' / ' + pad(rulerState.panels.length);
    }
    paintChrome(panel);
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function initRuler(scope) {
    if (scope !== document) return;
    measureChrome();
    window.addEventListener('resize', measureChrome, { passive: true });
    window.addEventListener('load', measureChrome);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureChrome);
    if ('ResizeObserver' in window) {
      var chrome = document.querySelector('[data-topbar]');
      if (chrome) new ResizeObserver(measureChrome).observe(chrome);
    }
    var ruler = $('[data-ruler]');
    var panels = $$('[data-panel]');
    if (!ruler) {
      if (panels.length) {
        // No ruler, but the header should still follow the panels.
        observePanels(panels, function (i) { paintChrome(panels[i]); });
      }
      return;
    }
    if (panels.length < 2) {
      ruler.hidden = true;
      if (panels.length) paintChrome(panels[0]);
      return;
    }

    ruler.hidden = false;
    var list = $('[data-ruler-ticks]', ruler);
    list.innerHTML = '';

    rulerState.panels = panels;
    rulerState.ticks = panels.map(function (panel, i) {
      if (!panel.id) panel.id = 'panel-' + (i + 1);
      var li = document.createElement('li');
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'ruler__tick';
      button.textContent = panel.getAttribute('data-panel-label') || pad(i + 1);
      button.addEventListener('click', function () {
        panel.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      });
      li.appendChild(button);
      list.appendChild(li);
      return button;
    });

    observePanels(panels, setActivePanel);

    var progress = $('[data-ruler-progress]', ruler);
    if (progress) {
      var ticking = false;
      var paint = function () {
        var max = document.documentElement.scrollHeight - window.innerHeight;
        var pct = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
        progress.style.width = (pct * 100).toFixed(2) + '%';
        ticking = false;
      };
      window.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(paint);
        },
        { passive: true }
      );
      paint();
    }
  }

  function observePanels(panels, onActive) {
    if (!('IntersectionObserver' in window)) {
      onActive(0);
      return;
    }
    var ratios = new Map();
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
        });
        var best = -1;
        var bestRatio = 0;
        panels.forEach(function (panel, i) {
          var r = ratios.get(panel) || 0;
          if (r > bestRatio) {
            bestRatio = r;
            best = i;
          }
        });
        if (best >= 0) onActive(best);
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: '-20% 0px -20% 0px' }
    );
    panels.forEach(function (panel) {
      observer.observe(panel);
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }


  /* ------------------------------------------------------------- carousels
     Horizontal rails you can drag, with a pair of round arrow buttons docked
     in the section heading. The nav only appears when there is something to
     scroll to, and it disables itself at each end. */

  var CAROUSEL_SELECTOR = '[data-carousel], .reviews__grid, .upsell';

  function buildArrow(direction) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'carousel__button';
    button.setAttribute('aria-label', direction === 'prev' ? 'Previous' : 'Next');
    button.innerHTML =
      '<svg class="icon" aria-hidden="true" focusable="false"><use href="#icon-arrow-' +
      (direction === 'prev' ? 'left' : 'right') +
      '"></use></svg>';
    return button;
  }

  function initCarousels(scope) {
    $$(CAROUSEL_SELECTOR, scope).forEach(function (track) {
      if (track.dataset.carouselReady === 'true') return;
      track.dataset.carouselReady = 'true';

      var section = track.closest('[data-section-id]') || track.parentNode;
      var head = section ? section.querySelector('.section__head') : null;

      var nav = document.createElement('div');
      nav.className = 'carousel__nav';
      var prev = buildArrow('prev');
      var next = buildArrow('next');
      nav.appendChild(prev);
      nav.appendChild(next);

      if (head) {
        head.appendChild(nav);
      } else {
        track.parentNode.insertBefore(nav, track);
      }

      function step() {
        var first = track.firstElementChild;
        var width = first ? first.getBoundingClientRect().width : track.clientWidth * 0.8;
        var styles = window.getComputedStyle(track);
        var gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;
        return Math.max(160, width + gap);
      }

      function sync() {
        var max = track.scrollWidth - track.clientWidth;
        var scrollable = max > 4;
        nav.style.display = scrollable ? '' : 'none';
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max - 2;
      }

      prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      });

      next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      });

      /* Cards parked off the right edge never intersect the viewport, so the
         reveal observer would leave them invisible. Reveal the whole rail as
         soon as any of it is on screen. */
      var revealAll = function () {
        $$('.reveal', track).forEach(function (el) { el.classList.add('is-visible'); });
        track.classList.add('is-visible');
      };

      if ('IntersectionObserver' in window) {
        var revealObserver = new IntersectionObserver(
          function (entries) {
            if (entries.some(function (e) { return e.isIntersecting; })) {
              revealAll();
              revealObserver.disconnect();
            }
          },
          { rootMargin: '0px 0px -10% 0px' }
        );
        revealObserver.observe(track);
      } else {
        revealAll();
      }

      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
      sync();
    });
  }


  /* ------------------------------------------------------- flight check
     The single biggest objection to a rideable case is "can I fly with it?".
     The picker answers it per airline instead of burying the rule in a
     collapsed notice. Status and note come straight off the option, so the
     merchant edits everything in the customiser. */

  var FC_LABELS = {
    cabin: 'Cleared for the cabin',
    checked: 'Allowed — check the case',
    ask: 'Check with the airline',
    no: 'Not permitted'
  };

  var FC_FALLBACK = {
    cabin: 'Remove the battery and carry it in your hand luggage. The case travels as normal luggage.',
    checked: 'Check the case into the hold and carry the battery in the cabin with you.',
    ask: 'This airline decides case by case. Call ahead and mention a 92.5Wh removable battery.',
    no: 'This airline does not accept a case with an integrated motor. Ship it ahead instead.'
  };

  function initFlightCheck(scope) {
    $$('[data-flight-check]', scope).forEach(function (root) {
      var select = $('[data-fc-select]', root);
      var result = $('[data-fc-result]', root);
      var badge = $('[data-fc-badge]', root);
      var note = $('[data-fc-note]', root);
      if (!select || !result) return;

      var idleBadge = badge ? badge.textContent : '';
      var idleNote = note ? note.textContent : '';

      function render() {
        var option = select.options[select.selectedIndex];
        var status = option ? option.getAttribute('data-status') : '';

        if (!select.value || !status) {
          result.setAttribute('data-state', 'idle');
          if (badge) badge.textContent = idleBadge;
          if (note) note.textContent = idleNote;
          return;
        }

        result.setAttribute('data-state', status);
        if (badge) badge.textContent = FC_LABELS[status] || status;
        if (note) {
          var custom = (option.getAttribute('data-note') || '').trim();
          note.textContent = custom || FC_FALLBACK[status] || '';
        }
      }

      on(select, 'change', render);
      render();
    });
  }

  function init(scope) {
    initLineReveal(scope);

    initParallax(scope);
    initCounters(scope);
    initStory(scope);
    initHotspots(scope);
    initRail(scope);
    initUpsell(scope);
    initLocale(scope);
    initReviewBars(scope);
    initRuler(scope);
    initCarousels(scope);
    initFlightCheck(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  theme.initSections = init;
  window.theme = theme;
})();
